import C from 'cesium';
import { find, forEach, hasIn, isNil, map } from 'lodash';
import turf from '@turf/turf';

import * as layerOps from '../../api/Layers/layer-operations';
import * as styleOps from '../../api/Styles/style-operations';
import { GEOJSON_GEOMETRIES } from '../../api/Styles/Styles';

import adaptURL from '../adapt-url';
import { getGeoJSONStyle } from '../geojson-layer-style';
import getGeoJSONFromCSV from '../get-geojson-from-csv';
import getGeoJSONFromExcel from '../get-geojson-from-excel';
import joinGeoJSONThematicLayer from '../join-geojson-thematic-layers';
import loadGeoJSONFromURL from '../load-geojson-from-url';
import loadStyleFromURL from '../load-style-from-url';
import methodPromise from '../method-promise-wrapper';

/**
 * Builds the GeoJSON layer.
 *
 * @async
 * @param {object} arg - The object argument.
 * @param {object} arg.layer - The layer object.
 * @param {object} arg.style - The style object.
 * @returns {Promise<object>} The leaflet layer to add to the map with the geojson and the style.
 */
const buildGeoJSONDataSource = async ({ layer, style }) => {
  let geojsonFile = '';
  let layerToDraw = '';
  let options;
  let customStyleObject;
  const url = adaptURL(layerOps.getURL(layer));
  try {
    switch (true) {
      case layerOps.isCSV(layer):
        geojsonFile = await getGeoJSONFromCSV(layer);
        break;
      case layerOps.isExcel(layer):
      case layerOps.isGoogleSheets(layer):
        geojsonFile = await getGeoJSONFromExcel(layer);
        break;
      case layerOps.isFiware(layer):
        geojsonFile = await methodPromise('layers.loadFiware', {
          addressIndex: layerOps.getAddressIndex(layer),
          fiwareLayerName: layerOps.getFiwareLayerName(layer),
          georefType: layerOps.getGeorefType(layer),
          loadParameters: {
            ...layerOps.getFiwareParams(layer),
            fiwareTokenURL: layerOps.getURL(layer)
          },
          locationIndex: layerOps.getLocationIndex(layer),
          spatialTableFields: layerOps.getTableSpatialObject(layer)
        });
        break;
      case layerOps.isGeoJSON(layer):
        geojsonFile = await loadGeoJSONFromURL(url);
        break;
      case layerOps.isJoin(layer):
        geojsonFile = await joinGeoJSONThematicLayer({
          geojsonId: layerOps.getGeoJSONId(layer),
          geojsonPropToJoin: layerOps.getGeoJSONPropToJoin(layer),
          layerSubType: layerOps.getSubType(layer),
          sorensenDiceMinimun: layerOps.getSorensenDiceMinimun(layer),
          thematicId: layerOps.getThematicId(layer),
          thematicKeyToJoin: layerOps.getThematicKeyToJoin(layer)
        });
        break;
      case layerOps.isOGR(layer):
        geojsonFile = await methodPromise('layers.loadOGRGeoJSON', {
          attribute: layerOps.getOGRAttributes(layer).split(','),
          layerName: layerOps.getOGRLayerName(layer),
          url
        });
        break;
      case layerOps.isPostgreSQL(layer):
        geojsonFile = await methodPromise('layers.loadPostgreSQL', {
          attributes: map(layerOps.getGeoJSONProps(layer), 'name'),
          loadParameters: layerOps.getConnectionParams(layer)
        });
        break;
      default:
        break;
    }

    if (styleOps.isCustom(style))
      customStyleObject = await loadStyleFromURL({
        layer,
        url: adaptURL(styleOps.getStyleURL(style))
      });

    const styleOptions = await getGeoJSONStyle({ customStyleObject, layer, style });

    options = {
      clampToGround: true,
      credit: layerOps.getAttribution(layer)
    };

    layerToDraw = new C.GeoJsonDataSource(layerOps.getName(layer));
    await layerToDraw.load(geojsonFile, options);

    const entities = layerToDraw.entities.values;
    forEach(entities, entity => {
      const properties = entity.properties.getValue('');
      const feature = find(geojsonFile.features, ['properties', properties]);
      if (isNil(feature)) return;
      const { color, fillColor, fillOpacity, opacity, weight } = styleOptions.style(feature);
      const featureType = turf.getType(feature);
      const material = !isNil(fillColor) && hasIn(fillColor, 'hex') ? fillColor.hex() : fillColor;
      /* eslint-disable no-param-reassign */
      switch (featureType) {
        case GEOJSON_GEOMETRIES.POLYGON:
        case GEOJSON_GEOMETRIES.MULTIPOLYGON:
          entity.polygon.material = C.Color.fromCssColorString(material).withAlpha(fillOpacity);
          entity.polygon.outlineColor = C.Color.fromCssColorString(color).withAlpha(opacity);
          entity.polygon.outlineWidth = weight;
          break;
        case GEOJSON_GEOMETRIES.LINESTRING:
          entity.polyline.material = C.Color.fromCssColorString(color).withAlpha(opacity);
          entity.polyline.width = weight;
          break;
        default:
          break;
      }
      /* eslint-enable no-param-reassign */
    });

    return layerToDraw;
  } catch (error) {
    console.error(error);
    throw new Error(error.message);
  }
};

export default buildGeoJSONDataSource;
